// =====================================================================
// importExport.js (E4) — bulk JSON/CSV export & import for the admin
// panel. No dependencies: RFC-4180 CSV writer/parser + JSON bundle
// helpers. File binaries are never exported (documents/photos stay in
// PocketBase storage) — metadata only.
// =====================================================================

// --- CSV -----------------------------------------------------------------

function csvEscape(value) {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function toCsv(rows, columns) {
  const head = columns.map((col) => csvEscape(col.label)).join(',');
  const body = rows.map((row) => columns.map((col) => csvEscape(col.value(row))).join(',')).join('\r\n');
  return `${head}\r\n${body}`;
}

// RFC-4180 parser: quoted fields, embedded commas/newlines/escaped quotes.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else {
      field += char;
    }
  }
  row.push(field);
  if (row.length > 1 || row[0] !== '') rows.push(row);

  const [header, ...rest] = rows;
  if (!header) return [];
  return rest.map((cells) => Object.fromEntries(header.map((key, index) => [key.trim(), cells[index] ?? ''])));
}

// --- download --------------------------------------------------------------

export function downloadFile(content, filename, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function stamp() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

export function exportCsv(filenameBase, rows, columns) {
  downloadFile(toCsv(rows, columns), `${filenameBase}-${stamp()}.csv`, 'text/csv;charset=utf-8');
}

// --- full business bundle (JSON) --------------------------------------------

// Serialises the tenant's whole dataset from the UI-shaped data object.
export function buildExportBundle(data) {
  return {
    format: 'portal-export',
    version: 1,
    exported_at: new Date().toISOString(),
    tenant: {
      name: data.tenant.name,
      slug: data.tenant.slug,
      address: data.tenant.address,
      contact_email: data.tenant.contact_email,
      contact_phone: data.tenant.contact_phone,
      telegram_chat_id: data.tenant.telegram_chat_id,
      invoice_prefix: data.tenant.invoice_prefix,
      next_invoice_number: data.tenant.next_invoice_number,
      payment_terms: data.tenant.payment_terms,
      tax_rate: data.tenant.tax_rate,
      payment_instructions: data.tenant.payment_instructions,
      sms_enabled: data.tenant.sms_enabled,
      key_date_types: data.tenant.key_date_types,
    },
    clients: data.clients.map(({ id, name, email, phone, address, notes, status }) => ({ id, name, email, phone, address, notes, status })),
    jobs: data.jobs.map(({ client_id, title, description, status, date, engineer, notes }) => ({ client_id, title, description, status, date, engineer, notes })),
    invoices: data.invoices.map(({ client_id, job_id, docType, number, title, status, due_date, paid_date, line_items }) => ({ client_id, job_id, doc_type: docType, number, title, status, due_date, paid_date, line_items: line_items.map(({ description, quantity, unit_price }) => ({ description, qty: quantity, unit_price })) })),
    payments: data.payments.map(({ client_id, invoice_id, amount, method, status, date, reference }) => ({ client_id, invoice_id, amount, method, status, date, reference })),
    keyDates: data.keyDates.map(({ client_id, type, date, detail, recurring }) => ({ client_id, type, date, detail, recurring })),
    messages: data.messages.map(({ client_id, sender, body, created_at }) => ({ client_id, sender, body, created_at })),
    documents: data.documents.map(({ client_id, type, filename, uploaded_at }) => ({ client_id, type, filename, uploaded_at })),
    notes: data.notes.map(({ client_id, author, body, pinned, client_visible, created_at }) => ({ client_id, author, body, pinned, client_visible, created_at })),
  };
}

export function exportBundle(data) {
  const bundle = buildExportBundle(data);
  downloadFile(JSON.stringify(bundle, null, 2), `portal-export-${stamp()}.json`, 'application/json');
  return bundle;
}

// --- column definitions (per screen CSV) -------------------------------------

export const COLUMNS = {
  clients: [
    { label: 'Name', value: (r) => r.name },
    { label: 'Email', value: (r) => r.email },
    { label: 'Phone', value: (r) => r.phone },
    { label: 'Address', value: (r) => r.address },
    { label: 'Status', value: (r) => r.status },
    { label: 'Notes', value: (r) => r.notes },
  ],
  jobs: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Title', value: (r) => r.title },
    { label: 'Property', value: (r, data) => (data.properties.find((p) => p.id === r.property_id)?.name || '') },
    { label: 'Status', value: (r) => r.status },
    { label: 'Date', value: (r) => r.date },
    { label: 'Scheduled start', value: (r) => r.scheduled_start },
    { label: 'Scheduled end', value: (r) => r.scheduled_end },
    { label: 'Completed', value: (r) => r.date_completed || '' },
    { label: 'Engineer', value: (r) => r.engineer },
    { label: 'Site address', value: (r) => [r.site_line1, r.site_line2, r.site_city, r.site_postcode].filter(Boolean).join(', ') },
    { label: 'Description', value: (r) => r.description },
  ],
  invoices: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Type', value: (r) => r.docType || 'invoice' },
    { label: 'Number', value: (r) => r.number },
    { label: 'Title', value: (r) => r.title },
    { label: 'Status', value: (r) => r.status },
    { label: 'Due date', value: (r) => r.due_date },
    { label: 'Total', value: (r) => r.line_items.reduce((s, i) => s + i.total, 0).toFixed(2) },
  ],
  payments: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Invoice', value: (r, data) => data.invoices.find((i) => i.id === r.invoice_id)?.number || '' },
    { label: 'Amount', value: (r) => r.amount },
    { label: 'Method', value: (r) => r.method },
    { label: 'Status', value: (r) => r.status },
    { label: 'Date', value: (r) => r.date },
    { label: 'Reference', value: (r) => r.reference },
  ],
  keyDates: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Type', value: (r) => r.type },
    { label: 'Title', value: (r) => r.title },
    { label: 'Date', value: (r) => r.date },
    { label: 'Recurring', value: (r) => (r.recurring ? 'yes' : 'no') },
    { label: 'Detail', value: (r) => r.detail },
  ],
  messages: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Sender', value: (r) => r.sender },
    { label: 'Name', value: (r) => r.name },
    { label: 'Message', value: (r) => r.body },
    { label: 'Sent', value: (r) => r.created_at },
    { label: 'Read', value: (r) => (r.read_at ? 'yes' : 'no') },
  ],
  documents: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Type', value: (r) => r.type },
    { label: 'Filename', value: (r) => r.filename },
    { label: 'Uploaded', value: (r) => r.uploaded_at },
  ],
  notes: [
    { label: 'Client', value: (r, data) => data.clients.find((c) => c.id === r.client_id)?.name || '' },
    { label: 'Author', value: (r) => r.author },
    { label: 'Note', value: (r) => r.body },
    { label: 'Pinned', value: (r) => (r.pinned ? 'yes' : 'no') },
    { label: 'Client visible', value: (r) => (r.client_visible ? 'yes' : 'no') },
    { label: 'Created', value: (r) => r.created_at },
  ],
};

// --- client CSV import --------------------------------------------------------

// Maps parsed CSV rows (Name/Email/Phone/Address/Status/Notes headers,
// case-insensitive) into UI-shape client values for add('clients', …).
export function mapClientCsvRows(rows) {
  const pick = (row, ...keys) => {
    for (const key of keys) {
      const match = Object.keys(row).find((k) => k.toLowerCase() === key.toLowerCase());
      if (match && row[match] !== '') return row[match];
    }
    return '';
  };
  return rows
    .map((row) => ({
      name: pick(row, 'name', 'client', 'full name'),
      email: pick(row, 'email', 'e-mail'),
      phone: pick(row, 'phone', 'telephone', 'mobile'),
      address: pick(row, 'address'),
      status: pick(row, 'status') || 'active',
      notes: pick(row, 'notes'),
    }))
    .filter((client) => client.name);
}
